import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import ActivityCard from '../components/courses/ActivityCard';
import ActivityDetailModal from '../components/courses/ActivityDetailModal';
import SectionFilter, { SectionFilterItem } from '../components/courses/SectionFilter';
import type { IActivityWithCourses, ICourse } from '../utils/courseSchedule';
import './ActivitiesPage.css';
import { translateError } from '../utils/errorMessages';

interface IPublicActivity {
  _id: number;
  name: string;
  description: string;
  coverImage?: string;
  joinPolicy: string;
  courses?: ICourse[];
}

const toActivityWithCourses = (a: IPublicActivity): IActivityWithCourses => ({
  _id: a._id,
  name: a.name,
  description: a.description,
  coverImage: a.coverImage,
  joinPolicy: a.joinPolicy,
  courses: a.courses || [],
});

/** Query param that deep-links a single activity's detail modal. */
const ACTIVITY_PARAM = 'activity';

const ActivitiesPage: React.FC = () => {
  const { t } = useTranslation();
  const [activities, setActivities] = useState<IPublicActivity[]>([]);
  const [sections, setSections] = useState<SectionFilterItem[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<IActivityWithCourses | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activityParam = searchParams.get(ACTIVITY_PARAM);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [activitiesRes, sectionsRes] = await Promise.all([
          api.activities.listPublic(),
          api.sections.listPublic().catch(() => ({ data: [] })),
        ]);
        setActivities(activitiesRes.data as IPublicActivity[]);
        setSections(sectionsRes.data as SectionFilterItem[]);
        setLoading(false);
      } catch (err: any) {
        setError(translateError(err, t, 'activities.loadError'));
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // The URL owns which activity is open, so a link like /activities?activity=12
  // restores the modal on load and browser back closes it. Only ids present in
  // the fetched list resolve — that list is already filtered to publicly visible
  // activities server-side, so a private id can never open a modal here.
  useEffect(() => {
    // Wait for the list; on a failed fetch leave the URL alone so a reload can
    // still resolve the link.
    if (loading || error) return;
    if (!activityParam) {
      setSelectedActivity(null);
      return;
    }
    const found = activities.find((a) => String(a._id) === activityParam);
    if (!found) {
      // Unknown, deleted or non-public id: drop the param and show the grid.
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(ACTIVITY_PARAM);
          return next;
        },
        { replace: true },
      );
      return;
    }
    // Keep the existing object when the id is unchanged: the modal's internal
    // state keys off the activity/courses it was given, so swapping in an equal
    // but fresh object on an unrelated re-render would reset it.
    setSelectedActivity((prev) =>
      prev && Number(prev._id) === Number(found._id) ? prev : toActivityWithCourses(found),
    );
  }, [activityParam, activities, loading, error, setSearchParams]);

  const openActivity = useCallback(
    (activity: IActivityWithCourses) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(ACTIVITY_PARAM, String(activity._id));
        return next;
      });
    },
    [setSearchParams],
  );

  const closeActivity = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(ACTIVITY_PARAM);
      return next;
    });
  }, [setSearchParams]);

  const visibleActivities = useMemo(() => {
    if (selectedSectionId == null) return activities;
    const section = sections.find((s) => s._id === selectedSectionId);
    if (!section) return activities;
    const allowed = new Set<number>(section.activityIds.map(Number));
    return activities.filter((a) => allowed.has(Number(a._id)));
  }, [activities, sections, selectedSectionId]);

  if (loading) {
    return (
      <div className="activities-container">
        <p className="loading-text">{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="activities-container">
        <p className="error-message">{error}</p>
      </div>
    );
  }

  return (
    <div className="activities-container">
      <div className="activities-header">
        <h1>{t('activities.title')}</h1>
      </div>

      <SectionFilter
        sections={sections}
        activities={activities}
        selectedId={selectedSectionId}
        onChange={setSelectedSectionId}
      />

      {visibleActivities.length === 0 ? (
        <div className="no-activities">
          <p>{t('activities.noPublicActivities')}</p>
        </div>
      ) : (
        <div className="activities-poster-grid">
          {visibleActivities.map((activity) => (
            <ActivityCard
              key={activity._id}
              activity={toActivityWithCourses(activity)}
              onSelect={openActivity}
            />
          ))}
        </div>
      )}

      <ActivityDetailModal
        activity={selectedActivity}
        onClose={closeActivity}
      />
    </div>
  );
};

export default ActivitiesPage;
